// MARK://dhk8/pos/lib/role-license-gate
// Single source of truth for "what can this user see in the POS shell".
//
// Inputs:
//   - the authenticated user's role (from `useAuthStore`)
//   - the active license features (SKU capabilities — `useLicenseStore`)
//
// Rules implemented (porter brief override 2026-05-11):
//   1. KDS is a feature flag.
//      • license.kds === true AND role is "cocina"  → ONLY `/kds` is allowed;
//        the user defaults to `/kds` and every other route redirects there.
//      • license.kds === false                       → `/kds` is forbidden for
//        everyone (redirect to the user's default route — a 403-equivalent).
//      • license.kds === true AND role is NOT cocina → normal POS, `/kds`
//        reachable as an expediter view.
//   2. A route that maps to a license feature the tenant doesn't have is
//      forbidden (e.g. `/reports` without `reports`, `/settings/printing`
//      without `printing`). When unknown, default to allowed (fail-open for
//      core POS flows so a missing license row doesn't brick the till).
//   3. Role can additionally restrict routes (e.g. a waiter never sees
//      `/manager`). Kept conservative — most POS routes are role-agnostic.
//
// Everything here is pure (no React); `App.tsx` wires `canAccessRoute` /
// `defaultRouteForUser` into the router, reading the stores at call time.

import type { PosUser } from '../stores/auth-store.js'
import { useLicenseStore, type PosLicense } from '../stores/license-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { getStoredJwt } from '../lib/api.js'
import { moduleForRoute, isAlwaysAllowedRoute } from '@dockethub/contracts/shared'

// ── Role normalisation ───────────────────────────────────────────────────
//
// Backend roles arrive as uppercase strings (KITCHEN, CASHIER, WAITER,
// MANAGER, ADMIN) — see sc-restaurant AppShell. The porter brief refers to the
// kitchen role as "cocina"; we accept both spellings here.

export type PosRole = 'cocina' | 'cajero' | 'mesero' | 'gerente' | 'admin' | 'desconocido'

export function normalizeRole(raw: string | null | undefined): PosRole {
  switch ((raw ?? '').trim().toUpperCase()) {
    case 'KITCHEN':
    case 'COCINA':
      return 'cocina'
    case 'CASHIER':
    case 'CAJERO':
      return 'cajero'
    case 'WAITER':
    case 'MESERO':
      return 'mesero'
    case 'MANAGER':
    case 'GERENTE':
      return 'gerente'
    case 'ADMIN':
    case 'OWNER':
    case 'SUPERADMIN':
      return 'admin'
    default:
      return 'desconocido'
  }
}

// ── Route → required license feature map ─────────────────────────────────
//
// Only routes that gate on a license feature appear here. Anything not listed
// is considered "core POS" and needs no feature flag.

const ROUTE_FEATURE: Record<string, keyof PosLicense> = {
  // NOTE: `/kds` is intentionally NOT here — KDS licensing + the role
  // exemptions (kitchen always; owner/admin/manager always) are handled by
  // "Rule 1 / Rule 1b" in `canAccessRoute`, which is finer-grained than this
  // flat map. Listing it here too would double-gate and block owner/admin.
  '/reports': 'reports',
  '/settings/printing': 'printing',
  // CFDI receipt extras live under /receipt but the route itself is core.
}

// Routes a given role may NOT access at all (independent of license).
// NOTE: `/settings/appearance` (the POS accent-color picker) is owner/manager-only:
// `gerente` == manager, `admin` == owner — everyone else is denied.
const ROLE_DENY: Record<PosRole, readonly string[]> = {
  // Kitchen is handled specially below (gets ONLY /kds). Listed for completeness.
  cocina: ['/floor', '/menu', '/order', '/cashout', '/cashcut', '/reports', '/manager', '/settings/printing', '/settings/appearance', '/offline/cola'],
  cajero: ['/manager', '/settings/appearance'],
  mesero: ['/manager', '/cashout', '/cashcut', '/reports', '/settings/printing', '/settings/appearance'],
  gerente: [],
  admin: [],
  desconocido: ['/settings/appearance'],
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Normalise a path to its top-level route key (e.g. `/order/42` → `/order`). */
export function routeKey(pathname: string): string {
  if (pathname === '/' || pathname === '') return '/'
  // `/settings/*` are two-segment keys; everything else is one segment.
  if (pathname.startsWith('/settings/printing')) return '/settings/printing'
  if (pathname.startsWith('/settings/appearance')) return '/settings/appearance'
  const seg = pathname.split('/').filter(Boolean)[0]
  return seg ? `/${seg}` : '/'
}

interface GateState {
  role: PosRole
  license: PosLicense
}

function readGateState(user?: PosUser | null, license?: PosLicense): GateState {
  const u = user ?? useAuthStore.getState().user
  const lic = license ?? useLicenseStore.getState().license
  return { role: normalizeRole(u?.role), license: lic }
}

// Demo / preview build: the dev-login user is a "Mesero Demo" but we want every
// module reachable for review — don't let role/license gating hide screens.
const DEMO_OPEN_ALL =
  import.meta.env['VITE_API_STUB'] === '1' || import.meta.env['VITE_DEV_LOGIN'] === '1'

// ── Módulos visibles desde el claim `modules` del JWT (#3 F3) ──────────────
// El backend (F2a) firma en el JWT el set de módulos del usuario (derivado del
// rol built-in, o del rol custom en F2b). Aquí lo decodificamos (sin verificar
// firma — el backend ya enforcea de verdad; esto es solo gating de UI) y lo
// memorizamos por token. Si el token no trae `modules` (sesión vieja) devolvemos
// undefined → canAccessRoute cae al deny-list legacy = cero regresión.
let _modToken: string | null = null
let _modValue: readonly string[] | undefined
function jwtModules(): readonly string[] | undefined {
  const tok = getStoredJwt()
  if (tok === _modToken) return _modValue
  _modToken = tok
  _modValue = undefined
  if (tok) {
    try {
      const part = tok.split('.')[1]
      if (part) {
        const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { modules?: unknown }
        if (Array.isArray(payload.modules)) {
          _modValue = payload.modules.filter((x): x is string => typeof x === 'string')
        }
      }
    } catch {
      /* token malformado → undefined → fallback legacy */
    }
  }
  return _modValue
}

/** True when `route` is reachable for the current (or supplied) user + license. */
export function canAccessRoute(pathname: string, user?: PosUser | null, license?: PosLicense): boolean {
  const { role, license: lic } = readGateState(user, license)
  const key = routeKey(pathname)

  // Demo build: every module is reachable for review.
  if (DEMO_OPEN_ALL) return true

  // Login is always reachable.
  if (key === '/login') return true

  // Rule 1: kitchen-locked user — ONLY /kds.
  // KDS is core POS for the kitchen role (see dhk8-platform-auth `/me/license`
  // comment: kitchen users must never be locked out of the whole POS). We do
  // NOT require `lic.kds === true` here: if the license row is missing or the
  // best-effort refresh failed, a kitchen user would otherwise be bounced to
  // `/login` after a *successful* login — a dead-end loop. Fail-open to `/kds`.
  if (role === 'cocina') {
    // Diagnóstico de impresión es la única excepción al lock de cocina: si su
    // impresora de comandas falla, el cocinero necesita poder verificar/probar.
    return key === '/kds' || key === '/diagnostico'
  }

  // Rule 1b: /kds is forbidden when the tenant isn't licensed for KDS.
  // Owner/admin/manager keep access regardless so they can demo/inspect the
  // KDS without switching users — KDS is part of every restaurant SKU.
  if (key === '/kds' && lic.kds !== true && role !== 'admin' && role !== 'gerente') {
    return false
  }

  // Rule 2: route maps to a license feature the tenant lacks.
  const feature = ROUTE_FEATURE[key]
  if (feature && lic[feature] !== true) return false

  // Rule 3: visibilidad por MÓDULO (#3 F3). Si el JWT trae el claim `modules`
  // (sesión nueva), gateamos por él: la ruta es visible solo si su módulo está
  // en el set del usuario. Rutas siempre-permitidas (diagnóstico) y rutas core
  // sin módulo asignado quedan abiertas (fail-open para no brickear la caja).
  const modules = jwtModules()
  if (modules !== undefined) {
    if (isAlwaysAllowedRoute('pos', pathname)) return true
    const mod = moduleForRoute('pos', pathname)
    if (mod === undefined) return true
    return modules.includes(mod)
  }

  // Fallback legacy (token sin `modules`): deny-list hardcodeada por rol.
  if (ROLE_DENY[role]?.includes(key)) return false

  // Root + anything else: allowed.
  return true
}

/** The route the user should land on after login (or when bounced off a blocked route). */
export function defaultRouteForUser(user?: PosUser | null, license?: PosLicense): string {
  const { role } = readGateState(user, license)

  // Kitchen role → straight to the KDS view, nothing else. Always `/kds`:
  // returning `/login` here would dead-end a kitchen user who just logged in
  // successfully (the gate above fail-opens `/kds` for the kitchen role).
  if (role === 'cocina') return '/kds'

  // Cashier / manager / admin / waiter → the floor map is the POS home.
  // Fall back through a small priority list in case `/floor` is somehow blocked.
  for (const candidate of ['/floor', '/order', '/menu', '/cashout', '/kds']) {
    if (canAccessRoute(candidate, user, license)) return candidate
  }
  // Rol muy restringido (custom) sin ninguna ruta operativa: el diagnóstico de
  // impresión siempre es alcanzable → evita el loop de redirección a /login.
  return '/diagnostico'
}

/**
 * Resolve where a navigation to `pathname` should actually go: the same path
 * if allowed, otherwise the user's default route (a clean redirect instead of
 * a hard 404). `App.tsx` calls this from its route guard.
 */
export function resolveRoute(pathname: string, user?: PosUser | null, license?: PosLicense): string {
  return canAccessRoute(pathname, user, license) ? pathname : defaultRouteForUser(user, license)
}
